import { useQuery, useMutation, useQueryClient } from "react-query";
import { makeAuthedRequest } from "../utils/makeAuthedRequest";
import { parseApiError } from "../utils/apiError";

export function useAddresses(options = {}) {
  const { enabled = true } = options;
  return useQuery(
    "addresses",
    async () => {
      const res = await makeAuthedRequest("/addresses");
      if (!res.ok) throw await parseApiError(res, "Addresses fetch failed");
      const payload = await res.json();
      const rawAddresses = Array.isArray(payload)
        ? payload
        : Array.isArray(payload?.addresses)
        ? payload.addresses
        : [];

      return rawAddresses.map((address, index) => ({
        ...address,
        id:
          address.id ??
          address.addressId ??
          address.iboAddressId ??
          address.contactId ??
          index,
        addressId:
          address.addressId ??
          address.iboAddressId ??
          address.id ??
          address.contactId ??
          index,
        address1: address.address1 ?? address.line1 ?? address.street ?? "",
        address2: address.address2 ?? address.line2 ?? address.street2 ?? "",
        zip: address.zip ?? address.zipCode ?? address.postalCode ?? "",
        country: address.country ?? address.countryCode ?? "",
        nickname: address.nickname ?? address.addressNickname ?? "",
        recipientName:
          address.recipientName ?? address.recipient ?? address.name ?? "",
        isPrimary: Boolean(address.isPrimary ?? address.primary),
        isDefault: Boolean(address.shipping),
      }));
    },
    { enabled }
  );
}

const computeBitwiseType = (types) => {
  let bitwiseType = 0;
  if (types.primary === 1) bitwiseType += 1; // 2^0
  if (types.mailing === 1) bitwiseType += 2; // 2^1
  if (types.shipping === 1) bitwiseType += 4; // 2^2
  if (types.billing === 1) bitwiseType += 8; // 2^3
  return bitwiseType;
};

export function useCreateAddress() {
  const queryClient = useQueryClient();
  return useMutation(
    async (payload) => {
      const types = {
        primary: Number(payload.primary ?? payload.isPrimary ?? 0),
        mailing: Number(payload.mailing ?? 0),
        shipping: Number(payload.shipping ?? 0),
        billing: Number(payload.billing ?? 0),
      };
      const normalizedPayload = {
        line1: payload.address1 ?? payload.line1 ?? payload.street ?? "",
        line2: payload.address2 ?? payload.line2 ?? payload.street2 ?? "",
        city: payload.city ?? "",
        state: payload.state ?? "",
        postalCode: payload.zip ?? payload.postalCode ?? payload.zipCode ?? "",
        country: payload.country ?? payload.countryCode ?? "US",
        recipient: payload.recipientName ?? payload.name ?? payload.recipient ?? "",
        nickname: payload.nickname ?? payload.addressNickname ?? "",
        types: {
          ...types,
          bitwiseType: computeBitwiseType(types),
        },
      };
      const res = await makeAuthedRequest("/addresses", {
        method: "POST",
        body: JSON.stringify(normalizedPayload),
      });
      if (!res.ok) throw await parseApiError(res, "Create address failed");
      const text = await res.text();
      return text ? JSON.parse(text) : null;
    },
    { onSuccess: () => queryClient.invalidateQueries("addresses") }
  );
}

export function useUpdateAddress() {
  const queryClient = useQueryClient();
  return useMutation(
    async ({ addressId, ...payload }) => {
      const types = {
        primary: Number(payload.primary ?? payload.isPrimary ?? 0),
        mailing: Number(payload.mailing ?? 0),
        shipping: Number(payload.shipping ?? 0),
        billing: Number(payload.billing ?? 0),
      };
      const normalizedPayload = {
        line1: payload.address1 ?? payload.line1 ?? payload.street ?? "",
        line2: payload.address2 ?? payload.line2 ?? payload.street2 ?? "",
        city: payload.city ?? "",
        state: payload.state ?? "",
        postalCode: payload.zip ?? payload.postalCode ?? payload.zipCode ?? "",
        country: payload.country ?? payload.countryCode ?? "US",
        recipient: payload.recipientName ?? payload.name ?? payload.recipient ?? "",
        nickname: payload.nickname ?? payload.addressNickname ?? "",
        types: {
          ...types,
          bitwiseType: computeBitwiseType(types),
        },
      };

      const res = await makeAuthedRequest(`/addresses/${addressId}`, {
        method: "PUT",
        body: JSON.stringify(normalizedPayload),
      });
      if (!res.ok) throw await parseApiError(res, "Update address failed");
      const text = await res.text();
      return text ? JSON.parse(text) : null;
    },
    { onSuccess: () => queryClient.invalidateQueries("addresses") }
  );
}

export function useDeleteAddress() {
  const queryClient = useQueryClient();
  return useMutation(
    async (addressId) => {
      const res = await makeAuthedRequest(`/addresses/${addressId}`, {
        method: "DELETE",
      });
      if (!res.ok) throw await parseApiError(res, "Delete address failed");
    },
    { onSuccess: () => queryClient.invalidateQueries("addresses") }
  );
}

export function useValidateAddress() {
  return useMutation(
    async (payload) => {
      const validatePayload = {
        line1: payload.address1 ?? payload.line1 ?? payload.street ?? "",
        line2: payload.address2 ?? payload.line2 ?? payload.street2 ?? "",
        city: payload.city ?? "",
        state: payload.state ?? "",
        postalCode: payload.zip ?? payload.postalCode ?? payload.zipCode ?? "",
        country: payload.country ?? payload.countryCode ?? "US",
      };
      const res = await makeAuthedRequest("/addresses/validate", {
        method: "POST",
        body: JSON.stringify(validatePayload),
      });
      if (!res.ok) throw await parseApiError(res, "Validate address failed");
      const data = await res.json();

      const standardized = data?.standardizedAddress;
      if (!standardized) {
        return { ...payload, isVerified: Boolean(data?.isVerified) };
      }

      const zip = standardized.ZIPPlus4
        ? `${standardized.ZIPCode}-${standardized.ZIPPlus4}`
        : standardized.ZIPCode ?? data.zip5 ?? "";

      return {
        ...payload,
        address1: standardized.streetAddress ?? payload.address1 ?? "",
        address2: standardized.secondaryAddress ?? payload.address2 ?? "",
        city: standardized.city ?? payload.city ?? "",
        state: standardized.state ?? payload.state ?? "",
        zip,
        isVerified: Boolean(data.isVerified),
      };
    },
    // Callers surface validation errors inline in the address form.
    { meta: { suppressErrorModal: true } }
  );
}
